import { useState } from 'react';
import {
  Button,
  ButtonIconPosition,
  ButtonStyles,
  Theme,
  ButtonWidth,
  useTranslation,
  useSession,
  AccountType,
  useNextgenRouter,
} from '@nextgen-web-framework/all';
import { StorageService, type AppState } from '@nextgen-web-framework/core';
import {
  CartEventsEnum,
  cartApi,
  CartConstantsEnum,
  ApiProjections,
  updateCart,
  publishLoaderEvent,
  OrderType,
  getActiveCart,
  deletePaymentDetails,
  MyShopStorageConstant,
  AccountProcessType,
  ExceptionsEnum,
  PublishEvent,
  createOrFetchCart,
} from '@commerce-cart-webcomponents/sdk';
import { useDispatch, useSelector } from 'react-redux';
import { AdvanceShareCodeResponse } from '@commerce-webcomponents/sdk';
import type { Cart, ErrorResponse, InitialCartState } from '@commerce-cart-webcomponents/sdk';
import { ButtonLoader } from '../../common/components/cart-loader/button-loader/ButtonLoader';
import { CartNotificationTheme } from '../../common/components/cart-notification/types/enums';
import { useCartNotification } from '../../common/hooks/useCartNotification';
import { TOAST_TIMEOUT } from '../../common/types/enums';
import { useExceptionToast } from '../../common';
import { RouteType } from '../mini-cart/types/enums';
import { AddToCartButtonPropTypes, MouseClickEventTyeSpan, MouseClickOrKeyPressEventTye } from './types/propsTypes';
import styles from './AddToCartButton.module.scss';
import { AddToCartButtonType } from './types/enums';

export const AddToCartButton = ({
  onProductAdded,
  closeDrawer,
  disabled = false,
  showRightIcon = false,
  className = '',
  entryObj,
  size,
  isMinProjection = false,
  title,
}: AddToCartButtonPropTypes) => {
  const t = useTranslation();
  const { isAnonymous } = useSession();
  const commerceCartState = useSelector((state: AppState) => state.commerceCart);
  const { cart }: InitialCartState = commerceCartState;
  const activeCart = cart.id ? getActiveCart(cart, '') : {};
  const { createToast, deleteToast } = useCartNotification('addToCartButton');
  const dispatch = useDispatch();
  const [showLoader, setShowLoader] = useState(false);
  const addToCartSuccess = t('cart.addToCartButton.addToCartSuccess');
  const addToCartButtonText = t('cart.addToCartButton.addToCart');
  const maxLimitReachedText = t('cart.addToCartButton.maxLimitText');
  const limitWillExceedText = t('cart.addToCartButton.limitExceedText');
  const maxLimitReachedHeading = t('cart.addToCartButton.maxLimitHeading');
  const limitWillExceedHeading = t('cart.addToCartButton.limitExceedHeading');
  const showSuccessMessage = addToCartSuccess;
  const triggerToast = useExceptionToast();
  const router = useNextgenRouter();

  const handleMaxQuantityError = (res: Cart, error?: ErrorResponse) => {
    let theme = CartNotificationTheme.RED;
    let heading = maxLimitReachedHeading;
    let title = maxLimitReachedText;

    if (!error) {
      return;
    }

    if (error?.code === ExceptionsEnum.Exceptions.ProductQtyPartiallyAdded) {
      title = limitWillExceedText;
      heading = limitWillExceedHeading;
      theme = CartNotificationTheme.SUCCESS;
    }

    createToast(
      {
        theme,
        rightSeperator: false,
        content: heading,
        clickIcon: 'times',
        icon: 'check-circle',
        subtext: title,
        timeOut: TOAST_TIMEOUT,
      },
      deleteToast,
    );
  };

  const updateCartWithRegistrationProduct = async (cartId: string) => {
    const shareCode = StorageService.getLocalStorage().retrieve(
      MyShopStorageConstant.SHARECODEATTRIBUTES,
    ) as AdvanceShareCodeResponse;
    if (isAnonymous() && shareCode && !cart.accountProcess) {
      const res = (await cartApi.updateCart(cartId, cartId, {
        accountProcess: {
          type: AccountProcessType.Registration,
          businessNature: (shareCode?.registrationAccountType ?? shareCode?.acctType) as AccountType,
        },
        orderType: OrderType.Regular,
        version: +StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartReqVersion),
      })) as Cart;
      dispatch(updateCart(res));
    }
  };

  const addToCart = async (e?: MouseClickOrKeyPressEventTye) => {
    let fetchCartRes = activeCart;
    const localstorage = StorageService.getLocalStorage();
    const cartIdInLS = localstorage.retrieve(CartConstantsEnum.CartId);
    if (!cartIdInLS) {
      const res = await createOrFetchCart();
      fetchCartRes = getActiveCart(res);
    }
    setShowLoader(true);
    const { productId, bundleSlots, quantity = 1 } = entryObj;

    const payload = {
      subCarts: [
        {
          subCartId: fetchCartRes?.subCartId ?? StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId),
          entries: [
            {
              product: { id: productId },
              quantity,
              bundleSlots,
            },
          ],
        },
      ],
      version: Number(StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartReqVersion)),
    };

    publishLoaderEvent(true);

    cartApi
      .addToCart({
        cartId: StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId),
        subCartId: StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId),
        projections:
          isMinProjection && !router.pathname?.includes(RouteType.CART) ? ApiProjections.MIN : ApiProjections.DETAILED,
        payload,
      })
      .then(async (res) => {
        const error = (res as Cart).subCarts?.[0].errors?.find(
          (error) =>
            error.code === ExceptionsEnum.Exceptions.ProductQtyPartiallyAdded ||
            error.code === ExceptionsEnum.Exceptions.ProductQtyLimitExceed,
        );
        updateCartWithRegistrationProduct((res as Cart).id);
        handleMaxQuantityError(res as Cart, error);
        const deletePaymentRes = await deletePaymentDetails(activeCart);
        const updatedRes = (deletePaymentRes ?? res) as Cart;
        !error &&
          createToast(
            {
              theme: CartNotificationTheme.SUCCESS,
              rightSeperator: false,
              content: showSuccessMessage,
              clickIcon: 'times',
              icon: 'check-circle',
              timeOut: TOAST_TIMEOUT,
            },
            deleteToast,
          );

        dispatch(updateCart(res as Cart));
        publishLoaderEvent(false);
        clickSuccessHandler(updatedRes, e);
        closeDrawer?.(e as MouseClickEventTyeSpan);
      })
      .catch(() => {
        setShowLoader(false);
        publishLoaderEvent(false);
        triggerToast();
        closeDrawer?.(e as MouseClickEventTyeSpan);
      });
  };

  const getProductEntry = (cart: Cart) => {
    const activeCart = cart.subCarts?.find((subCart) => subCart.subCartId === cart.id);
    return activeCart?.entries?.find((entry) => entryObj.productId === entry.product.id);
  };

  const clickSuccessHandler = (res: Cart, e?: MouseClickOrKeyPressEventTye) => {
    setShowLoader(false);
    const entry = getProductEntry(res);
    PublishEvent({
      cartEvent: CartEventsEnum.AddToCart,
      cartEntry: entry,
      cartId: res.id,
      cart: res,
    });

    if (e && onProductAdded) {
      onProductAdded(e, res);
    }
    // CB for Product team to close drawer.
    closeDrawer?.();
  };
  return (
    <>
      <div className={`${styles['add-to-cart-container']} ${className}`}>
        <div className={styles['container']}>
          {showLoader ? (
            <ButtonLoader size={size} />
          ) : (
            <Button
              id={'add-to-cart-button'}
              onClick={(event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
                addToCart(event);
              }}
              buttonWidth={ButtonWidth.FULL_WIDTH}
              title={title ?? addToCartButtonText}
              buttonStyle={ButtonStyles.SOLID}
              theme={Theme.BLACK}
              icon={showRightIcon ? { iconName: 'cart-shopping' } : undefined}
              iconPosition={ButtonIconPosition.RIGHT}
              size={size}
              tabIndex={0}
              disabled={disabled}
              aria-label="Add to Cart Button"
              role="button"
            />
          )}
        </div>
      </div>
    </>
  );
};

export type { AddToCartButtonPropTypes };
export { AddToCartButtonType };
